class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval) || 5000;
    this.observeHeight();
    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    if (this.dataset.mode === 'slider' && this.slides.length > 1) this.initSlider();
    if (this.dataset.mode === 'split') this.initSplit();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
    this.splitObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  // Split layout: both sides stay on one line while they fit. Otherwise "both" shrinks them
  // until they fit, and the other options mark the bar is-cramped to rotate the sides or
  // show the right side only (CSS).
  initSplit() {
    this.bar = this.querySelector('[data-split-bar]');
    this.sides = [...(this.bar?.querySelectorAll('[data-split-side]') || [])];
    if (this.sides.length < 2) return;
    this.splitIndex = 0;
    this.sides[0].classList.add('is-shown');
    const fits = this.dataset.splitMobile === 'both';
    const fit = () => {
      const style = getComputedStyle(this.bar);
      const gap = parseFloat(style.columnGap) || 0;
      const room = this.bar.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0) - gap;
      const needed = this.sides.reduce((total, side) => total + side.scrollWidth, 0);
      return { room, needed };
    };
    const measure = () => {
      const wasCramped = this.classList.contains('is-cramped');
      this.classList.remove('is-cramped', 'is-tight');
      this.bar.style.removeProperty('--split-scale');
      let { room, needed } = fit();
      let cramped = needed > room + 1;
      if (cramped && fits) {
        this.classList.add('is-tight');
        ({ room, needed } = fit());
        // Text and icons scale together but not quite linearly, so settle in a few passes.
        let scale = 1;
        for (let pass = 0; pass < 4 && needed > room + 1 && scale > 0.8; pass += 1) {
          scale = Math.max(0.8, Math.floor(scale * (room / needed) * 100) / 100);
          this.bar.style.setProperty('--split-scale', String(scale));
          ({ room, needed } = fit());
        }
        cramped = false;
      }
      this.classList.toggle('is-cramped', cramped);
      if (cramped !== wasCramped) this.start();
    };
    if ('ResizeObserver' in window) {
      let lastWidth = 0;
      this.splitObserver = new ResizeObserver(() => {
        const width = this.bar.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        measure();
      });
      this.splitObserver.observe(this.bar);
    }
    measure();
    document.fonts?.ready.then(measure);
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.start();
    });
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  showSide(index) {
    this.splitIndex = ((index % this.sides.length) + this.sides.length) % this.sides.length;
    this.sides.forEach((side, i) => {
      const shown = i === this.splitIndex;
      side.classList.toggle('is-shown', shown);
      side.inert = this.classList.contains('is-cramped') && !shown;
    });
  }

  splitRotates() {
    return this.dataset.mode === 'split' && this.dataset.splitMobile === 'rotate' && this.classList.contains('is-cramped');
  }

  observeHeight() {
    if (!('ResizeObserver' in window)) return;
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? this.offsetHeight);
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
    });
    this.resizeObserver.observe(this);
  }

  initSlider() {
    this.viewport = this.querySelector('[data-viewport]');
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, -1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, 1, true));

    const pause = () => this.stop();
    const resume = () => this.start();
    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) resume();
    });

    let startX = null;
    let startY = 0;
    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        this.stop();
      },
      { passive: true }
    );
    this.viewport.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const delta = event.changedTouches[0].clientX - startX;
      const vertical = Math.abs(event.changedTouches[0].clientY - startY);
      startX = null;
      if (Math.abs(delta) > 40 && Math.abs(delta) > vertical) this.go(this.index + (delta < 0 ? 1 : -1), delta < 0 ? 1 : -1, true);
      this.start();
    });

    document.addEventListener('visibilitychange', this.onVisibility);
    this.start();
  }

  start() {
    this.stop();
    if (this.dataset.mode === 'split') {
      if (!this.sides) return;
      if (!this.splitRotates()) {
        this.sides.forEach((side) => (side.inert = false));
        return;
      }
      this.showSide(this.splitIndex);
      if (this.editorPinned || document.hidden) return;
      const turn = () => {
        this.showSide(this.splitIndex + 1);
        this.timer = setTimeout(turn, this.interval);
      };
      this.timer = setTimeout(turn, this.interval);
      return;
    }
    if (this.dataset.autoplay !== 'true' || this.editorPinned || document.hidden) return;
    this.viewport?.setAttribute('aria-live', 'off');
    const tick = () => {
      this.go(this.index + 1, 1);
      this.timer = setTimeout(tick, this.interval);
    };
    this.timer = setTimeout(tick, this.interval);
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  go(target, direction = 1, manual = false) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    if (next === this.index) return;
    cancelAnimationFrame(this.frame);
    const current = this.slides[this.index];
    const incoming = this.slides[next];
    this.style.setProperty('--slide-dir', direction);

    this.slides.forEach((slide) => {
      if (slide === incoming) return;
      slide.classList.remove('is-active', 'is-preparing');
      if (slide !== current) {
        clearTimeout(slide.leaveTimer);
        slide.classList.remove('is-leaving');
      }
      slide.setAttribute('aria-hidden', 'true');
      slide.inert = true;
    });

    clearTimeout(incoming.leaveTimer);
    incoming.classList.remove('is-leaving', 'is-active');
    incoming.classList.add('is-preparing');
    current.classList.add('is-leaving');
    void incoming.offsetWidth;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      incoming.classList.remove('is-preparing');
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');
      incoming.inert = false;
    });

    clearTimeout(current.leaveTimer);
    current.leaveTimer = setTimeout(() => current.classList.remove('is-leaving'), 750);
    this.index = next;
    if (manual) this.viewport?.setAttribute('aria-live', 'polite');
  }

  selectBlock(blockId) {
    if (this.sides) {
      const side = this.sides.findIndex((node) => node.dataset.shopifyEditorBlock?.includes(blockId));
      if (side < 0) return;
      this.editorPinned = true;
      this.stop();
      this.showSide(side);
      return;
    }
    const index = this.slides.findIndex(
      (slide) =>
        slide.dataset.shopifyEditorBlock?.includes(blockId) ||
        [...slide.querySelectorAll('[data-shopify-editor-block]')].some((side) => side.dataset.shopifyEditorBlock.includes(blockId))
    );
    if (index < 0) return;
    this.editorPinned = true;
    this.stop();
    this.go(index, index > this.index ? 1 : -1);
  }

  deselectBlock() {
    this.editorPinned = false;
    this.start();
  }
}

if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const bar = event.target.closest?.('announcement-bar');
    bar?.selectBlock?.(event.detail.blockId);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest?.('announcement-bar')?.deselectBlock?.();
  });
}
